import { useState, useMemo, useRef, type ReactElement } from 'react';
import { Bot, Regex, Check } from 'lucide-react';
import type { NetworkConnectionListItem } from '@kirin/api-client/contracts/shadow-ai';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

import {
  getShadowAiCategoryLabel,
  shadowAiCategoryLabels,
} from '@kirin/api-client/contracts/shadow-ai';

interface IdentifyAgentModalProps {
  connection: NetworkConnectionListItem | null;
  allDomains: string[];
  existingAgentNames: Array<{ name: string; category: string | null }>;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    agentName: string,
    matchedDomains: string[],
    options?: { category?: string }
  ) => void;
  isLoading: boolean;
  /**
   * KIR-3496: when opened from the bulk "Identify All" action, the exact set of
   * selected domains to identify together. Overrides the single-connection
   * default so the whole selection is classified (symmetric with "Dismiss
   * All"). Omitted for the per-row Identify action.
   */
  presetDomains?: string[];
}

const CATEGORIES = Object.entries(shadowAiCategoryLabels)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

function globToRegex(pattern: string): RegExp {
  const escaped = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.');
  return new RegExp(`^${escaped}$`, 'i');
}

function getMatchingDomains(pattern: string, domains: string[]): string[] {
  if (!pattern.trim()) return [];
  try {
    const regex = globToRegex(pattern);
    return domains.filter(domain => regex.test(domain));
  } catch {
    return [];
  }
}

function similarityScore(a: string, b: string): number {
  const al = a.toLowerCase();
  const bl = b.toLowerCase();
  if (al === bl) return 100;
  if (al.includes(bl) || bl.includes(al)) return 80;
  const aWords = al.split(/[\s\-_]+/);
  const bWords = bl.split(/[\s\-_]+/);
  const matches = aWords.filter(w =>
    bWords.some(bw => bw.includes(w) || w.includes(bw))
  );
  if (matches.length > 0) return 50 + matches.length * 10;
  return 0;
}

export function IdentifyAgentModal({
  connection,
  allDomains,
  existingAgentNames,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  presetDomains,
}: IdentifyAgentModalProps): ReactElement {
  const connectionKey = connection?.domain ?? '';
  const defaultName = connection?.processName || connection?.domain || '';
  const [agentName, setAgentName] = useState(defaultName);
  const [category, setCategory] = useState<string>('chatbot');
  const [isCategoryLocked, setIsCategoryLocked] = useState(false);
  const [usePattern, setUsePattern] = useState(false);
  const [domainPattern, setDomainPattern] = useState(connectionKey);
  const [lastConnectionKey, setLastConnectionKey] = useState(connectionKey);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when connection changes (React pattern for derived state)
  if (connectionKey !== lastConnectionKey) {
    setAgentName(defaultName);
    setCategory('agent');
    setIsCategoryLocked(false);
    setDomainPattern(connectionKey);
    setUsePattern(false);
    setLastConnectionKey(connectionKey);
  }

  const filteredSuggestions = useMemo(() => {
    const processName = connection?.processName || '';
    let filtered = existingAgentNames;
    if (agentName.trim()) {
      const lower = agentName.toLowerCase();
      filtered = existingAgentNames.filter(item =>
        item.name.toLowerCase().includes(lower)
      );
    }
    if (processName) {
      return [...filtered].sort(
        (a, b) =>
          similarityScore(b.name, processName) -
          similarityScore(a.name, processName)
      );
    }
    return filtered;
  }, [agentName, existingAgentNames, connection?.processName]);

  const matchingDomains = useMemo(() => {
    if (usePattern) return getMatchingDomains(domainPattern, allDomains);
    // Bulk "Identify All" → identify the whole selection; otherwise the single
    // connection the modal was opened for.
    if (presetDomains && presetDomains.length > 0) return presetDomains;
    return connection ? [connection.domain] : [];
  }, [usePattern, domainPattern, allDomains, connection, presetDomains]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm(agentName || defaultName, matchingDomains, { category });
  };

  const handleSelectSuggestion = (item: {
    name: string;
    category: string | null;
  }) => {
    setAgentName(item.name);
    if (item.category) {
      setCategory(item.category);
      setIsCategoryLocked(true);
    } else {
      setIsCategoryLocked(false);
    }
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleNameChange = (val: string) => {
    setAgentName(val);
    setShowSuggestions(true);

    // If typing manually, check if it matches an existing agent exactly
    const exactMatch = existingAgentNames.find(
      item => item.name.toLowerCase() === val.toLowerCase()
    );
    if (exactMatch && exactMatch.category) {
      setCategory(exactMatch.category);
      setIsCategoryLocked(true);
    } else {
      setIsCategoryLocked(false);
    }
  };

  if (!connection) {
    return <></>;
  }

  const canConfirm = matchingDomains.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-blue-600" />
            Identify AI Agent
          </DialogTitle>
          <DialogDescription>
            Mark network traffic as coming from an AI agent. It will be tracked
            in your Tools dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="agent-name">Agent Name</Label>
            <Popover open={showSuggestions && filteredSuggestions.length > 0}>
              <PopoverAnchor asChild>
                <Input
                  ref={inputRef}
                  id="agent-name"
                  value={agentName}
                  onChange={e => handleNameChange(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() =>
                    setTimeout(() => setShowSuggestions(false), 150)
                  }
                  placeholder={defaultName}
                  autoComplete="off"
                />
              </PopoverAnchor>
              <PopoverContent
                className="p-0 w-[var(--radix-popover-trigger-width)]"
                align="start"
                onOpenAutoFocus={e => e.preventDefault()}
              >
                <div className="max-h-48 overflow-y-auto">
                  {filteredSuggestions.map(item => (
                    <button
                      key={item.name}
                      type="button"
                      className={cn(
                        'w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center justify-between',
                        agentName === item.name && 'bg-muted'
                      )}
                      onClick={() => handleSelectSuggestion(item)}
                    >
                      <span>{item.name}</span>
                      <div className="flex items-center gap-2">
                        {item.category && (
                          <span className="text-[10px] tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            {getShadowAiCategoryLabel(item.category)}
                          </span>
                        )}
                        {agentName === item.name && (
                          <Check className="h-4 w-4" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={category}
              onValueChange={setCategory}
              disabled={isCategoryLocked}
            >
              <SelectTrigger id="category" className="w-full">
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isCategoryLocked && (
              <p className="text-[10px] text-muted-foreground italic">
                Category is locked for existing tool
              </p>
            )}
          </div>

          {existingAgentNames.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Select an existing agent or type a new name
            </p>
          )}

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="use-pattern" className="cursor-pointer">
                Use wildcard pattern
              </Label>
              <p className="text-xs text-muted-foreground">
                Match multiple domains with * and ? wildcards
              </p>
            </div>
            <Switch
              id="use-pattern"
              checked={usePattern}
              onCheckedChange={setUsePattern}
            />
          </div>

          {usePattern ? (
            <div className="space-y-2">
              <Label htmlFor="domain-pattern">
                <span className="flex items-center gap-1.5">
                  <Regex className="h-3.5 w-3.5" />
                  Domain Pattern
                </span>
              </Label>
              <Input
                id="domain-pattern"
                value={domainPattern}
                onChange={e => setDomainPattern(e.target.value)}
                placeholder="*.openai.com"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Use <code className="bg-muted px-1 rounded">*</code> for any
                characters, <code className="bg-muted px-1 rounded">?</code> for
                single character
              </p>
            </div>
          ) : (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground mb-1">
                Process → Host
              </div>
              <div className="font-mono text-sm">
                {connection.processName && (
                  <>
                    <span className="text-blue-600 dark:text-blue-400">
                      {connection.processName}
                    </span>
                    <span className="text-muted-foreground mx-1">→</span>
                  </>
                )}
                <span>{connection.domain}</span>
              </div>
            </div>
          )}

          {usePattern && (
            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Matching Domains</span>
                <span
                  className={cn(
                    'text-xs px-2 py-0.5 rounded-full',
                    matchingDomains.length > 0
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                  )}
                >
                  {matchingDomains.length} match
                  {matchingDomains.length !== 1 ? 'es' : ''}
                </span>
              </div>
              <div className="max-h-32 overflow-y-auto">
                {matchingDomains.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">
                    No domains match this pattern
                  </p>
                ) : (
                  <ul className="text-xs font-mono space-y-1">
                    {matchingDomains.slice(0, 10).map(domain => (
                      <li
                        key={domain}
                        className="text-muted-foreground truncate"
                      >
                        {domain}
                      </li>
                    ))}
                    {matchingDomains.length > 10 && (
                      <li className="text-muted-foreground italic">
                        +{matchingDomains.length - 10} more...
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading || !canConfirm}>
            {isLoading
              ? 'Identifying...'
              : matchingDomains.length > 1
                ? `Identify ${matchingDomains.length} Domains`
                : 'Identify as AI Agent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
